import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Alert,
  AlertTitle,
  AlertDescription,
} from 'prosci-ui';
import { CircleHelp } from '../../utils/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useHubAppCopy } from '../../hooks';

interface CertificationCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { firstName: string; lastName: string; alternateEmail?: string }) => void;
  loading?: boolean;
}

export const CertificationCheckModal: React.FC<CertificationCheckModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false
}) => {
  const { user } = useAuth();
  const hubAppCopy = useHubAppCopy();
  const copy = hubAppCopy('portalCopy')?.subscriptionCopy?.certification?.check;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [alternateEmail, setAlternateEmail] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!firstName.trim()) {
      newErrors.firstName = copy?.validation?.firstNameRequired || 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = copy?.validation?.lastNameRequired || 'Last name is required';
    }

    if (alternateEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(alternateEmail)) {
      newErrors.alternateEmail = copy?.validation?.invalidEmail || 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      alternateEmail: alternateEmail.trim() || undefined
    });
  };

  // Build description with email interpolation
  const descriptionTemplate = copy?.description || "To be eligible for this tier, you must have completed the Practitioner Program. We don't have a record of {{email}} completing the program. If you completed it under a different email address, please provide that below.";
  const description = descriptionTemplate.replace('{{email}}', user?.email || 'your account');

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy?.title || 'Have you completed Practitioner Program?'}</DialogTitle>
          <DialogDescription dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description.replace(user?.email || 'your account', `<strong>${user?.email || 'your account'}</strong>`)) }} />
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field className="space-y-2">
              <FieldLabel htmlFor="firstName" required>
                {copy?.labels?.firstName || 'First Name'}
              </FieldLabel>
              <Input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={loading}
                aria-invalid={!!errors.firstName}
                aria-describedby={errors.firstName ? 'firstName-error' : undefined}
              />
              {errors.firstName && <FieldError id="firstName-error">{errors.firstName}</FieldError>}
            </Field>

            <Field className="space-y-2">
              <FieldLabel htmlFor="lastName" required>
                {copy?.labels?.lastName || 'Last Name'}
              </FieldLabel>
              <Input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
                aria-invalid={!!errors.lastName}
                aria-describedby={errors.lastName ? 'lastName-error' : undefined}
              />
              {errors.lastName && <FieldError id="lastName-error">{errors.lastName}</FieldError>}
            </Field>
          </div>

          <Field className="space-y-2">
            <FieldLabel htmlFor="alternateEmail">
              {copy?.labels?.alternateEmail || 'Alternative Email'}
            </FieldLabel>
            <Input
              id="alternateEmail"
              type="email"
              value={alternateEmail}
              onChange={(e) => setAlternateEmail(e.target.value)}
              disabled={loading}
              aria-invalid={!!errors.alternateEmail}
              aria-describedby={errors.alternateEmail ? 'alternateEmail-error' : undefined}
            />
            {errors.alternateEmail && (
              <FieldError id="alternateEmail-error">{errors.alternateEmail}</FieldError>
            )}
          </Field>

          <Alert variant="neutral">
            <CircleHelp />
            <AlertTitle>{copy?.starterNotice?.title || "Haven't completed the Practitioner Program?"}</AlertTitle>
            <AlertDescription>
              {copy?.starterNotice?.description || 'You can still join the Starter Membership today!'}
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              {copy?.buttons?.cancel || 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
            >
              {loading ? (copy?.buttons?.checking || 'Checking...') : (copy?.buttons?.check || 'Check Verification')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CertificationCheckModal;
